/**
 * Buy List Price Sync Service
 * 
 * Syncs buy list item prices with current market prices when DailyPrices.lastUpdated
 * is newer than the list's lastUpdated timestamp.
 */

import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { recalculateCalcItemPrices } from '../utils/calculatorFormula';
import IDX from '../utils/indices';

const PRICE_KEYS = [
    'priceUSD', 'priceEUR', 'priceGBP', 'priceAUD', 'priceCAD', 'priceJPY', 'priceAED', 'priceRUB',
    'priceCNY', 'priceKRW', 'priceINR', 'priceZAR', 'priceBRL', 'priceMXN', 'priceMYR', 'priceSEK',
    'priceDKK', 'priceNOK', 'priceCHF', 'pricePLN', 'priceCZK', 'priceBGN', 'priceHRK', 'priceTRY',
    'priceRON', 'priceSAR', 'priceKWD', 'priceJOD', 'priceEGP', 'priceMAD', 'priceDZD', 'priceTND',
    'priceETB', 'priceUZS',
];

/**
 * Extract price fields from an Algolia hit
 */
function extractPricesFromHit(hit) {
    const prices = {};
    const source = hit?.data || hit;
    for (const key of PRICE_KEYS) {
        const val = Number(source?.[key]);
        if (Number.isFinite(val)) {
            prices[key] = val;
        }
    }
    return prices;
}

function extractDiscountFromHit(hit) {
    const source = hit?.data || hit;
    const discount = Number(source?.discount ?? 0);
    return Number.isFinite(discount) ? discount : 0;
}

/**
 * Determine which Algolia index to use based on material type
 */
function getIndexForMaterial(material) {
    const mat = (material || '').toUpperCase();
    if (mat === 'METAL') return IDX.metals;
    if (mat === 'TRUCK') return IDX.trucks;
    if (mat === 'BOARD' || mat === 'BOARDS' || mat === 'ELECTRONIC' || mat === 'ELECTRONICS') return IDX.boards;
    return IDX.converters;
}

/**
 * Check if prices need syncing based on list's lastUpdated vs DailyPrices.lastUpdated
 * 
 * @param {string} uid - User ID
 * @param {string} listName - Name of the list
 * @param {number} dailyPricesLastUpdated - DailyPrices.lastUpdated timestamp
 * @param {string} listsField - 'lists' or 'listsBoards'
 * @returns {Promise<boolean>}
 */
export async function checkNeedsPriceSync(uid, listName, dailyPricesLastUpdated, listsField = 'lists') {
    if (!uid || !listName || !dailyPricesLastUpdated) {
        return false;
    }

    try {
        const userRef = doc(db, 'users', uid);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.data();
        const listData = userData?.[listsField]?.[listName];
        // Ensure both are numbers for proper comparison
        const listLastUpdated = Number(listData?.lastUpdated) || 0;
        const dailyTs = Number(dailyPricesLastUpdated) || 0;
        return dailyTs > listLastUpdated;
    } catch (e) {
        console.error('[PriceSync] Error checking need for sync:', e);
        return false;
    }
}

/**
 * Update the list's lastUpdated timestamp after sync
 * 
 * @param {string} uid - User ID
 * @param {string} listName - Name of the list
 * @param {number} timestamp - New timestamp (usually DailyPrices.lastUpdated)
 * @param {string} listsField - 'lists' or 'listsBoards'
 */
export async function updateListLastUpdated(uid, listName, timestamp, listsField = 'lists') {
    if (!uid || !listName) return;

    try {
        const userRef = doc(db, 'users', uid);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.data() || {};

        // Get current lists object and update the specific list's lastUpdated
        const lists = { ...(userData[listsField] || {}) };
        if (lists[listName]) {
            lists[listName] = { ...lists[listName], lastUpdated: timestamp };

            // Update the entire lists field to avoid dot notation issues with list names containing dots
            await updateDoc(userRef, { [listsField]: lists });
        }
    } catch (e) {
        console.error('[PriceSync] Error updating list lastUpdated:', e);
    }
}

/**
 * Sync all buy list item prices with current market prices.
 * 
 * - Calculator items: Recalculate using local formula
 * - Converter/truck/metal items: Fetch fresh prices from Algolia
 * 
 * @param {string} uid - User ID
 * @param {string} listName - Buy list name
 * @param {Array} items - Buy list items
 * @param {Object} dailyPrices - DailyPrices document
 * @param {Object} charges - Charges document
 * @param {Object} algoliaClient - Algolia client instance (from component)
 * @param {string} collectionName - 'buyLists' or 'buyListsBoards'
 * @returns {Promise<{synced: number, failed: number, skipped: number}>}
 */
export async function syncBuyListPrices(uid, listName, items, dailyPrices, charges, algoliaClient, collectionName = 'buyLists') {
    const result = { synced: 0, failed: 0, skipped: 0 };

    if (!uid || !listName || !items.length) {
        return result;
    }

    for (const item of items) {
        const material = String(item.material || '').toUpperCase();

        try {
            let newPrices = null;
            let newDiscount = Number(item?.discount ?? 0) || 0;

            if (material === 'CALCULATOR') {
                // Calculator items: use local formula
                if (item.weightCeramic && (item.pt || item.pd || item.rh)) {
                    newPrices = recalculateCalcItemPrices(
                        {
                            pt: item.pt || 0,
                            pd: item.pd || 0,
                            rh: item.rh || 0,
                            weightCeramic: Number(item.weightCeramic) || 0,
                            moisture: item.moisture || 0,
                            shippingCost: item.shippingCost,
                        },
                        dailyPrices,
                        charges
                    );
                }
            } else if (algoliaClient) {
                // Regular converter/truck/metal items: fetch from Algolia
                const indexName = getIndexForMaterial(material);

                try {
                    // Use algoliasearch v5 API - direct getObject method
                    const hit = await algoliaClient.getObject({
                        indexName: indexName,
                        objectID: item.id,
                    });
                    if (hit) {
                        newPrices = extractPricesFromHit(hit);
                        newDiscount = extractDiscountFromHit(hit);
                    }
                } catch (algoliaErr) {
                    // Item might not exist in Algolia anymore
                    console.warn(`[PriceSync] Could not fetch ${item.id} from Algolia:`, algoliaErr?.message || algoliaErr);
                }
            }

            if (newPrices && Object.keys(newPrices).length > 0) {
                // Check if prices actually changed before updating
                const oldUSD = Number(item.prices?.priceUSD ?? item.priceUSD ?? 0);
                const newUSD = Number(newPrices.priceUSD ?? 0);
                const oldDiscount = Number(item?.discount ?? 0) || 0;
                const discountChanged = Math.abs(oldDiscount - newDiscount) > 0.0001;

                // Only update if a price-affecting snapshot field actually changed.
                if (Math.abs(oldUSD - newUSD) > 0.01 || discountChanged) {
                    const itemRef = doc(db, collectionName, uid, listName, item.id);
                    await updateDoc(itemRef, { prices: newPrices, discount: newDiscount });
                    result.synced++;
                } else {
                    // Prices are the same - no need to update
                    result.skipped++;
                }
            } else {
                result.skipped++;
            }
        } catch (e) {
            console.error(`[PriceSync] Failed to sync item ${item.id}:`, e);
            result.failed++;
        }
    }

    return result;
}

/**
 * Check if prices need syncing by comparing sample items against live Algolia data.
 * More reliable than timestamp comparison - actually verifies if prices changed.
 *
 * @param {Array} items - Buy list items
 * @param {Object} algoliaClient - Algolia client instance
 * @param {string} listsField - 'lists' or 'listsBoards' (used for fallback timestamp check)
 * @returns {Promise<boolean>} - True if sync is needed
 */
export async function shouldSyncPrices(items, algoliaClient) {
    if (!items || items.length === 0 || !algoliaClient) {
        return false;
    }

    // Filter out calculator items (they use DailyPrices for recalculation, not Algolia)
    const nonCalcItems = items.filter(item =>
        String(item.material || '').toUpperCase() !== 'CALCULATOR'
    );

    // If only calculator items, recalculate one and compare to see if DailyPrices changed
    if (nonCalcItems.length === 0) {
        try {
            const { dailyPrices, charges } = await loadPriceData();
            // Pick the first calculator item that has PPM data
            const sample = items.find(i =>
                i.weightCeramic && (i.pt || i.pd || i.rh)
            );
            if (!sample) return false; // No valid calculator items

            const recalculated = recalculateCalcItemPrices(
                {
                    pt: sample.pt || 0,
                    pd: sample.pd || 0,
                    rh: sample.rh || 0,
                    weightCeramic: Number(sample.weightCeramic) || 0,
                    moisture: sample.moisture || 0,
                    shippingCost: sample.shippingCost,
                },
                dailyPrices,
                charges
            );
            const storedUSD = Number(sample.prices?.priceUSD ?? sample.priceUSD ?? 0);
            const recalcUSD = Number(recalculated?.priceUSD ?? 0);
            const needsSync = Math.abs(storedUSD - recalcUSD) > 0.01;
            console.log(`[PriceSync] Calculator sample check: stored=${storedUSD}, recalc=${recalcUSD}, needsSync=${needsSync}`);
            return needsSync;
        } catch (e) {
            console.warn('[PriceSync] Calculator sample check failed:', e?.message || e);
            return true; // Safety fallback
        }
    }

    // Pick up to 3 random items for sampling (any type, including calculator)
    const sampleSize = Math.min(3, items.length);
    const shuffled = [...items].sort(() => Math.random() - 0.5);
    const samples = shuffled.slice(0, sampleSize);

    let checked = 0;
    let mismatched = 0;
    let priceData = null; // Lazy-loaded for calculator items

    for (const item of samples) {
        try {
            const material = String(item.material || '').toUpperCase();

            if (material === 'CALCULATOR') {
                // Calculator items: recalculate locally and compare
                if (!item.weightCeramic || (!item.pt && !item.pd && !item.rh)) {
                    continue;
                }
                if (!priceData) {
                    priceData = await loadPriceData();
                }
                const recalculated = recalculateCalcItemPrices(
                    {
                        pt: item.pt || 0,
                        pd: item.pd || 0,
                        rh: item.rh || 0,
                        weightCeramic: Number(item.weightCeramic) || 0,
                        moisture: item.moisture || 0,
                        shippingCost: item.shippingCost,
                    },
                    priceData.dailyPrices,
                    priceData.charges
                );
                const storedUSD = Number(item.prices?.priceUSD ?? item.priceUSD ?? 0);
                const recalcUSD = Number(recalculated?.priceUSD ?? 0);
                checked++;
                if (Math.abs(storedUSD - recalcUSD) > 0.01) {
                    mismatched++;
                    console.log(`[PriceSync] Calculator sample mismatch for ${item.id}: stored=${storedUSD}, recalc=${recalcUSD}`);
                }
            } else {
                // Regular items: fetch from Algolia and compare
                const indexName = getIndexForMaterial(item.material);
                const hit = await algoliaClient.getObject({
                    indexName,
                    objectID: item.id,
                });

                if (!hit) {
                    console.log(`[PriceSync] Sample item ${item.id} not found in Algolia, skipping`);
                    continue;
                }

                const source = hit?.data || hit;
                const livePrice = Number(source?.priceUSD ?? 0);
                const storedPrice = Number(item.prices?.priceUSD ?? item.priceUSD ?? 0);

                checked++;

                if (Math.abs(livePrice - storedPrice) > 0.01) {
                    mismatched++;
                    console.log(`[PriceSync] Sample mismatch for ${item.id}: stored=${storedPrice}, live=${livePrice}`);
                }
            }
        } catch (e) {
            // Item deleted or fetch failed - skip to next sample
            console.warn(`[PriceSync] Sample check failed for ${item.id}:`, e?.message || e);
            continue;
        }
    }

    // Sync if ANY sample has different price
    // Also sync if we couldn't verify any items (safety fallback - e.g., all items deleted)
    const needsSync = mismatched > 0 || checked === 0;
    console.log(`[PriceSync] Sample check: checked=${checked}, mismatched=${mismatched}, needsSync=${needsSync}`);
    return needsSync;
}

/**
 * Load daily prices and charges from Firestore
 */
export async function loadPriceData() {
    const [pricesSnap, chargesSnap] = await Promise.all([
        getDoc(doc(db, 'prices', 'DailyPrices')),
        getDoc(doc(db, 'prices', 'Charges')),
    ]);

    const dailyPrices = pricesSnap.exists() ? pricesSnap.data() : {};
    const charges = chargesSnap.exists() ? chargesSnap.data() : {};
    const lastUpdated = Number(dailyPrices.lastUpdated) || 0;

    return { dailyPrices, charges, lastUpdated };
}
